import type { ReactNode } from 'react';
import { Popup } from '../Popup';
import { Button } from './Button';
import './Popups.css';

// ──────────────────────────────────────────────
// ConfirmPopup — title, message, primary + ghost buttons, optional 3rd
// ──────────────────────────────────────────────

interface ConfirmPopupProps {
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Extra action slot (e.g. "Restart" next to "Exit"). */
  tertiaryLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  onTertiary?: () => void;
  /** Marks the confirm as destructive (red styling). */
  destructive?: boolean;
  /** Shows an ✕ close button in the header. */
  closable?: boolean;
}

/**
 * Confirm dialog with title, optional message, and primary/ghost buttons.
 * Use the optional `tertiary` slot for three-way choices like Exit/Restart/Cancel.
 */
export function ConfirmPopup({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tertiaryLabel,
  onConfirm,
  onCancel,
  onTertiary,
  destructive,
  closable = true,
}: ConfirmPopupProps) {
  return (
    <Popup aria-label={typeof title === 'string' ? title : undefined} onDismiss={onCancel} maxWidth={360}>
      <Popup.Header title={title} onClose={closable ? onCancel : undefined} />
      {message && (
        <Popup.Body>
          <p className="ui-confirm__message">{message}</p>
        </Popup.Body>
      )}
      <Popup.Footer>
        <Button
          variant={destructive ? 'ghost' : 'primary'}
          size="wide-sm"
          onClick={onConfirm}
          className={destructive ? 'ui-confirm__destructive' : undefined}
        >
          {confirmLabel}
        </Button>
        {tertiaryLabel && onTertiary && (
          <Button variant="ghost" size="wide-sm" onClick={onTertiary}>
            {tertiaryLabel}
          </Button>
        )}
        <Button variant="ghost" size="wide-sm" onClick={onCancel}>
          {cancelLabel}
        </Button>
      </Popup.Footer>
    </Popup>
  );
}

// ──────────────────────────────────────────────
// InfoPopup — title + body + single close button
// ──────────────────────────────────────────────

interface InfoPopupProps {
  title: ReactNode;
  message?: ReactNode;
  closeLabel?: string;
  onClose: () => void;
  children?: ReactNode;
}

/** Informational popup with a title, optional body, and a single close button. */
export function InfoPopup({
  title,
  message,
  closeLabel = 'OK',
  onClose,
  children,
}: InfoPopupProps) {
  return (
    <Popup aria-label={typeof title === 'string' ? title : undefined} onDismiss={onClose} maxWidth={360}>
      <Popup.Header title={title} onClose={onClose} />
      {(message || children) && (
        <Popup.Body>
          {message && <p className="ui-confirm__message">{message}</p>}
          {children}
        </Popup.Body>
      )}
      <Popup.Footer>
        <Button variant="primary" size="wide-sm" onClick={onClose}>
          {closeLabel}
        </Button>
      </Popup.Footer>
    </Popup>
  );
}

// ──────────────────────────────────────────────
// SheetPopup — title + scrollable body + optional footer
// ──────────────────────────────────────────────

interface SheetPopupProps {
  title: ReactNode;
  onClose: () => void;
  /** Extra action slot in the header (e.g. a secondary button). */
  headerAction?: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
  children: ReactNode;
}

/**
 * Long-content sheet popup with pinned header, scrollable body, and an
 * optional pinned footer. Used for Settings, Quick Guide, etc.
 */
export function SheetPopup({
  title,
  onClose,
  headerAction,
  footer,
  maxWidth = 640,
  children,
}: SheetPopupProps) {
  return (
    <Popup
      aria-label={typeof title === 'string' ? title : undefined}
      onDismiss={onClose}
      maxWidth={maxWidth}
    >
      <Popup.Header title={title} onClose={onClose} action={headerAction} />
      <Popup.Body scrollable>{children}</Popup.Body>
      {footer && <Popup.Footer>{footer}</Popup.Footer>}
    </Popup>
  );
}
